import React, { useState } from 'react';
import { Box, Button, Chip, Divider, Menu, MenuItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import { autoColor, toneOf } from './ReportLabelsCell';
import type { ReportLabel } from '../../../rtk-apis/reports/reportLabels';

/**
 * Label filter for the reports list.
 *
 * A native <select> was used first and looked wrong: the browser renders that popup with
 * OS chrome — white background, square blue highlight — which reads as a system dialog
 * dropped onto a dark clinical list. A menu we own lets the options render as the same
 * chips shown in the Labels column, so the filter looks like the thing it filters.
 */
interface Props {
  labels: ReportLabel[];
  /** Selected label id, or '' for everything. */
  value: string;
  onChange: (labelId: string) => void;
}

export default function ReportLabelFilter({ labels, value, onChange }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const selected = labels.find(l => l._id === value) || null;
  const isActive = !!selected;

  const pick = (labelId: string) => {
    onChange(labelId);
    setAnchor(null);
  };

  const chipSx = (label: ReportLabel) => {
    const tone = toneOf(label.color || autoColor(label._id));
    return {
      height: 21,
      maxWidth: 150,
      bgcolor: tone.bg,
      color: tone.fg,
      border: `1px solid ${tone.border}`,
      fontSize: 11,
      fontWeight: 600,
    };
  };

  return (
    <>
      <Button
        onClick={e => setAnchor(e.currentTarget)}
        startIcon={<LabelOutlinedIcon sx={{ fontSize: 15 }} />}
        endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
        sx={{
          height: 34,
          px: 1.5,
          borderRadius: '9px',
          textTransform: 'none',
          fontSize: 12.5,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          // An applied filter is called out in the accent so it is obvious the list is
          // narrowed — the commonest confusion with a filter that lives on the toolbar.
          color: isActive ? '#5eead4' : 'rgba(148,163,184,0.95)',
          border: `1px solid ${isActive ? 'rgba(45,212,191,0.55)' : 'rgba(148,163,184,0.22)'}`,
          bgcolor: isActive ? 'rgba(45,212,191,0.10)' : 'rgba(148,163,184,0.06)',
          '&:hover': {
            bgcolor: isActive ? 'rgba(45,212,191,0.16)' : 'rgba(148,163,184,0.12)',
            borderColor: 'rgba(45,212,191,0.45)',
          },
        }}
      >
        {selected ? selected.name : 'All labels'}
      </Button>

      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 220,
              maxHeight: 340,
              bgcolor: '#0b1b2b',
              backgroundImage: 'none',
              border: '1px solid rgba(148,163,184,0.22)',
              borderRadius: 2,
              boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
            },
          },
        }}
      >
        <Typography
          sx={{
            px: 1.75,
            pt: 1,
            pb: 0.75,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'rgba(148,163,184,0.75)',
          }}
        >
          Filter by label
        </Typography>

        <MenuItem
          onClick={() => pick('')}
          sx={{ gap: 1, py: 0.75, fontSize: 13, color: '#e2e8f0' }}
        >
          <CheckIcon sx={{ fontSize: 15, color: value ? 'transparent' : '#5eead4' }} />
          All labels
        </MenuItem>

        <Divider sx={{ borderColor: 'rgba(148,163,184,0.18)' }} />

        {labels.map(label => (
          <MenuItem
            key={label._id}
            onClick={() => pick(label._id)}
            sx={{ gap: 1, py: 0.6 }}
          >
            <CheckIcon
              sx={{
                fontSize: 15,
                flexShrink: 0,
                color: value === label._id ? '#5eead4' : 'transparent',
              }}
            />
            <Chip
              size="small"
              label={label.name}
              sx={chipSx(label)}
            />
          </MenuItem>
        ))}

        {!labels.length && (
          <Box sx={{ px: 1.75, py: 1.5, maxWidth: 240 }}>
            <Typography sx={{ fontSize: 12, color: 'rgba(148,163,184,0.75)' }}>
              No labels yet. Add one from a report to start filtering.
            </Typography>
          </Box>
        )}
      </Menu>
    </>
  );
}
